'use client'

import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { cn } from '@kiao-voice/ui-kit'
import { TITLE_COMPONENTS } from './title-markup'

/**
 * 產品頁首屏。水墨是 hero 自己的背景，跟文字同一個空間，不是旁邊另一塊面板。
 *
 * 去背圖（紙白已是透明）加一層橢圓遮罩，不用 `mix-blend-mode`、不疊多層遮罩：
 * 那兩樣在不同瀏覽器表現不一，失效時紙白會原樣畫出來、切出一條直邊。遮罩中心
 * 在右半幅，往左在文字欄結束前就淡到全透明，所以墨與文字永不重疊。
 *
 * `titleKey` 是 i18n key 而不是已經算好的字串：行銷站的襯線標題走 subset 字型，
 * 字集掃描器（scripts/lib/collect-display-charset.mjs）只認得出原始碼裡的字面
 * key。傳 ReactNode 進來會讓標題的字掉出 subset，漏字就地退成系統襯線。
 */

export interface InkHeroProps {
  /** eyebrow 文字（已 t() 過；mono 小字不吃襯線 subset）。 */
  eyebrow: string
  /**
   * 標題的 i18n key。值裡可用 `<ph>` 包片語（inline-block，片語放得下就不拆）、
   * `<br/>` 指定斷行、`<em>` 標重點（品牌紅底線，不是斜體）。
   */
  titleKey: string
  lead?: string
  /** CTA 列。主按鈕是品牌紅，每頁只有一顆。 */
  actions?: React.ReactNode
  meta?: React.ReactNode
  /** 文字欄寬。墨在文字結束之後才開始，標題較短的頁面收窄到 34em。 */
  copyWidth?: 'default' | 'narrow' | 'wide'
  /** `capped` 把標題壓在 56px 以內，給本來就比較長的兩行標題。 */
  titleSize?: 'display' | 'capped' | 'compact'
  /** `roomy` 給第一行帶底線的標題：1.16 的行高會讓紅線幾乎貼到第二行字頂。 */
  titleLeading?: 'tight' | 'roomy'
  /** 沒有按鈕列的短首頁 hero，在手機保留標題右側的墨。 */
  inkPlacement?: 'default' | 'short'
}

export function InkHero({
  eyebrow,
  titleKey,
  lead,
  actions,
  meta,
  copyWidth = 'default',
  titleSize = 'display',
  titleLeading = 'tight',
  inkPlacement = 'default',
}: InkHeroProps) {
  const { t } = useTranslation(['web'])
  const shortWithoutControls = inkPlacement === 'short' && !actions && !meta

  return (
    <section
      className={cn(
        'relative isolate bg-background pt-[calc(var(--section-space)*1.5)] pb-(--section-space)',
        inkPlacement === 'short' ? '[@media(max-width:860px)]:pt-7' : 'max-[860px]:pt-7',
        shortWithoutControls && '[@media(max-width:860px)]:pb-0'
      )}
    >
      {/* 墨。手機降到五成濃度、只鋪在標題右邊的空白處，導言之前就淡掉。 */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 -z-10 bg-no-repeat',
          "bg-[url('/campaign/ink-wave-alpha.webp')] bg-[position:right_-14%_center] bg-[size:min(76%,1120px)_auto]",
          '[mask-image:radial-gradient(ellipse_36%_54%_at_83%_48%,black_36%,transparent_100%)]',
          inkPlacement === 'short'
            ? '[@media(max-width:860px)]:opacity-50 [@media(max-width:860px)]:bg-[size:190%_auto] [@media(max-width:860px)]:bg-[position:22%_30%] [@media(max-width:860px)]:[mask-image:radial-gradient(ellipse_44%_32%_at_84%_36%,black_32%,transparent_100%)]'
            : 'max-[860px]:opacity-50 max-[860px]:bg-[size:190%_auto] max-[860px]:bg-[position:28%_16%] max-[860px]:[mask-image:radial-gradient(ellipse_46%_24%_at_80%_20%,black_30%,transparent_100%)]'
        )}
      />
      <div className="mx-auto w-full max-w-(--container) px-5 min-[561px]:px-(--gutter)">
        <div
          className={cn(
            'max-w-[38em]',
            copyWidth === 'narrow' && 'max-w-[34em]',
            copyWidth === 'wide' && 'max-w-[42em]'
          )}
        >
          <p className="mb-(--gap-md) font-mono text-xs tracking-[0.28em] text-muted uppercase">
            {eyebrow}
          </p>
          <h1
            data-display-font=""
            className={cn(
              'mb-(--gap-md) font-display font-semibold tracking-[0.005em]',
              titleSize === 'display' &&
                'text-(length:--fs-marketing-h1) max-[360px]:text-[calc(var(--fs-marketing-h1)*0.9)]',
              titleSize === 'capped' && 'text-[length:clamp(32px,4.4vw,56px)]',
              titleSize === 'compact' && 'text-[length:clamp(30px,4.1vw,52px)]',
              titleLeading === 'roomy' ? 'leading-[1.32]' : 'leading-[1.16]',
              inkPlacement === 'short' &&
                '[@media(max-width:860px)]:max-w-[6.6em] [@media(max-width:860px)]:[&_br]:hidden'
            )}
          >
            <Trans t={t} i18nKey={titleKey} components={TITLE_COMPONENTS} />
          </h1>
          {lead ? (
            <p
              className={cn(
                'mb-(--gap-lg) max-w-[34em] text-[length:var(--fs-marketing-lead)] leading-[1.7] text-muted',
                shortWithoutControls && '[@media(max-width:860px)]:mb-0'
              )}
            >
              {lead}
            </p>
          ) : null}
          {actions ? (
            <div className="inline-flex flex-wrap items-center gap-(--gap-sm)">{actions}</div>
          ) : null}
          {meta ? (
            <p className="mt-(--gap-md) font-mono text-(length:--fs-meta) text-muted">{meta}</p>
          ) : null}
        </div>
      </div>
    </section>
  )
}
